/**
 * mockupChanges.js — atomic client-feedback lifecycle for mockups.
 *
 * Every write to a mockup's `clientChanges[]` and the derived per-post
 * `clientStatus` goes through the `mark_mockup_change` Postgres RPC (atomic,
 * SECURITY DEFINER). This is the same pattern the client review page already
 * uses for `mark_mockup_post`, and it is what stops a whole-store save from
 * clobbering (or being clobbered by) a concurrent client action — the bug that
 * made client changes vanish and reappear.
 *
 * Actions:
 *   resolve  — a manager turned the change into a task   { subTaskId, taskId }
 *   dismiss  — a manager dismissed the change (no task)
 *   submit   — the designer submitted the fix            { empId, link }
 *   reopen   — send the fix back to the designer
 *   done     — the manager approved the fix              { empId }
 *
 * On `done` the RPC recomputes the post's `clientStatus`: it only flips to
 * 'changed' (the purple "please re-check" state) when EVERY change on that post
 * is done AND the post's cover image actually changed since the client asked.
 * Otherwise the post stays 'edit' (amber — still action required).
 *
 * Returns the RPC payload: { ok, action, coverChanged, allDone, change } — or
 * throws so the caller can surface a save failure.
 */
import { supabase, ORG_ID } from './supabaseClient';

export async function markMockupChange(mockId, changeId, action, extra = {}) {
  const { data, error } = await supabase.rpc('mark_mockup_change', {
    p_org: ORG_ID, p_mock: mockId, p_change: changeId, p_action: action, p_extra: extra,
  });
  if (error) throw new Error(error.message || 'mark_mockup_change failed');
  return data;
}

// Manager manual override for a single post's client status. Atomic, so it is not
// clobbered by a whole-store save (which always preserves clientStatus from the DB).
// status '' clears it; 'changed' turns the post purple ("change made · re-check") and
// closes any still-open client changes on that post.
export async function setMockupCellStatus(mockId, cellId, status, extra = {}) {
  const { data, error } = await supabase.rpc('set_mockup_cell_status', {
    p_org: ORG_ID, p_mock: mockId, p_cell: cellId, p_status: status, p_extra: extra,
  });
  if (error) throw new Error(error.message || 'set_mockup_cell_status failed');
  return data;
}

// Publish / re-publish: stamp ONLY the share metadata (shareId/shareUrl/publishedAt)
// atomically. Publishing used to write back a whole-mockup snapshot captured BEFORE
// the (slow) HTML upload, which reverted any post/cover/caption edited during the
// upload. This RPC can never touch the cells, so publishing can't revert content.
export async function markMockupPublished(mockId, shareId, shareUrl, publishedAt) {
  const { error } = await supabase.rpc('mark_mockup_published', {
    p_org: ORG_ID, p_id: mockId, p_share_id: shareId, p_share_url: shareUrl, p_published_at: publishedAt,
  });
  if (error) throw new Error(error.message || 'mark_mockup_published failed');
}

// Atomic per-cell content write (cover/caption/link/reel/carousel). These fields are
// OWNED by this RPC — mockup_save_keep_feedback leaves them alone — so a stale
// whole-store save can never revert a cover/caption/link. Fire-and-forget from the
// editor (optimistic UI already applied); throws so the caller can flag a save error.
export async function setMockupCellFields(mockId, cellId, fields) {
  const { error } = await supabase.rpc('set_mockup_cell_fields', {
    p_org: ORG_ID, p_mock: mockId, p_cell: cellId, p_fields: fields || {},
  });
  if (error) throw new Error(error.message || 'set_mockup_cell_fields failed');
}

// Atomic write of the cell LIST — which cells exist and in what order (add row /
// remove post / reorder). The list is OWNED by this RPC: mockup_save_keep_feedback no
// longer adds, removes or reorders, so a lagging tab can never write its own older
// ordering and push the live cells in behind it. Each surviving cell keeps the CONTENT
// the DB already holds (that belongs to set_mockup_cell_fields), so this only ever
// moves structure. This is the fix for the Buttr grid scrambling itself on 2026-09-30.
export async function setMockupCells(mockId, cells) {
  const { error } = await supabase.rpc('set_mockup_cells', {
    p_org: ORG_ID, p_id: mockId, p_cells: Array.isArray(cells) ? cells : [],
  });
  if (error) throw new Error(error.message || 'set_mockup_cells failed');
}

// Client-facing status for a single change (used by the review-page change log).
export function changeClientStatus(change) {
  if (!change) return 'received';
  if (change.done) return 'done';
  if (change.dismissed) return 'noted';
  if (change.resolved) return 'in_progress';
  return 'received';
}
